// ABC files and tunebooks. One `.abc` or `.abct` file may hold many tunes,
// each opening with an `X:` line; an optional FILE HEADER before the first
// tune sets defaults (L:, M:, R:, %% directives…) for every tune in it.
//
// Splitting reads no music: the index lists titles and composers cheaply and
// keeps each tune's text as a self-contained one-tune file (the CHUNK, with
// the file header's defaults written in), so a tune parses on demand and a
// playlist row can carry exactly the tune it names.

import { decodeAbcText, normalizeAbcTitle, stripComment } from './text';

export interface AbcTuneSource {
  /** First title, decoded and article-normalised ("The Kesh"). */
  title: string;
  /** C: lines joined with ", ", or '' when the tune names none. */
  composer: string;
  /** A self-contained one-tune ABC file: header defaults + the tune. */
  chunk: string;
}

export interface AbcBook {
  /** The file header's T: or B: line, when it has one. */
  name?: string;
  tunes: AbcTuneSource[];
}

const FIELD = /^([A-Za-z+]):(.*)$/;

/** Header fields that change how a tune parses or what it is called. */
const HEADER_DEFAULTS = new Set(['C', 'L', 'M', 'Q', 'R', 'I', 'U', 'O', 'A']);

function lines(source: string): string[] {
  return source
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .split('\n');
}

const isBlank = (line: string) => line.trim() === '';

function fieldOf(line: string): { letter: string; value: string } | null {
  const m = FIELD.exec(line.trimStart());
  return m ? { letter: m[1], value: m[2].trim() } : null;
}

/** Does this line belong to a tune body (music, a field, a comment)? */
function isTuneContent(line: string): boolean {
  const t = line.trimStart();
  return FIELD.test(t) || t.startsWith('%') || t.startsWith('[') || t.includes('|');
}

/**
 * Name an unnamed tunebook the ABC way, with a B: (book) line in its file
 * header — so the name is part of the text and survives being stored and
 * read back. A book that names itself keeps its own name.
 */
export function withAbcBookName(source: string, name: string | undefined): string {
  const clean = name?.replace(/\.(?:abct?|txt)$/i, '').trim();
  if (!clean) return source;
  for (const line of lines(source)) {
    if (/^\s*X:/.test(line)) break;
    if (/^\s*[TB]:\s*\S/.test(line)) return source;
  }
  return `B:${clean}\n${source}`;
}

/** Is this text ABC (rather than an iReal link or MusicXML)? */
export function isAbcSource(text: string): boolean {
  const head = text.slice(0, 200_000);
  if (/^\s*(?:irealb|irealbook):\/\//i.test(head) || /^\s*</.test(head)) return false;
  return /^[ \t]*K:/m.test(head) && /^[ \t]*[XTML]:/m.test(head);
}

/**
 * Split an ABC file into its tunes. A tune runs from its X: line to the next
 * one; a blank line ends it early only when what follows is not tune content,
 * so commentary between tunes is dropped while a tune that leaves blank lines
 * between its systems stays whole. A fragment with no X: line but a K: field
 * is read as a single tune.
 */
export function splitAbcBook(source: string): AbcBook {
  const all = lines(source);
  const starts: number[] = [];
  all.forEach((line, i) => {
    if (/^\s*X:/.test(line)) starts.push(i);
  });
  if (starts.length === 0) {
    if (!all.some((line) => /^\s*K:/.test(line))) return { tunes: [] };
    const tune = tuneOf(all, [], 0, all.length);
    return { tunes: tune ? [tune] : [] };
  }

  const header = all.slice(0, starts[0]).filter((line) => {
    const t = line.trimStart();
    return t.startsWith('%%') || (FIELD.test(t) && !t.startsWith('X:'));
  });
  let name: string | undefined;
  for (const line of header) {
    const field = fieldOf(line);
    if (field && (field.letter === 'T' || field.letter === 'B') && field.value) {
      name = decodeAbcText(stripComment(field.value)).trim() || undefined;
      if (name) break;
    }
  }

  const tunes: AbcTuneSource[] = [];
  starts.forEach((start, k) => {
    const end = k + 1 < starts.length ? starts[k + 1] : all.length;
    const tune = tuneOf(all, header, start, end);
    if (tune) tunes.push(tune);
  });
  return { ...(name ? { name } : {}), tunes };
}

function tuneOf(all: string[], header: string[], start: number, end: number): AbcTuneSource | null {
  const body: string[] = [];
  let inBody = false;
  for (let i = start; i < end; i++) {
    const line = all[i];
    if (isBlank(line)) {
      if (!inBody) continue;
      let next = i + 1;
      while (next < end && isBlank(all[next])) next++;
      if (next >= end || !isTuneContent(all[next])) break;
      body.push('');
      continue;
    }
    const field = fieldOf(line);
    if (field?.letter === 'K' || (!field && !line.trimStart().startsWith('%'))) inBody = true;
    body.push(line);
  }
  if (!body.some((line) => /^\s*K:/.test(line))) {
    // A tune with no key field is still music when it has music lines.
    if (!body.some((line) => !fieldOf(line) && !line.trimStart().startsWith('%'))) return null;
  }

  let title = '';
  const composers: string[] = [];
  const own = new Set<string>();
  for (const line of body) {
    const field = fieldOf(line);
    // T: and C: may follow K: until the music starts.
    if (!field) {
      if (line.trim() && !line.trimStart().startsWith('%')) break;
      continue;
    }
    own.add(field.letter);
    const value = decodeAbcText(stripComment(field.value)).trim();
    if (field.letter === 'T' && !title && value) title = normalizeAbcTitle(value);
    if (field.letter === 'C' && value) composers.push(value);
  }
  // The file header's defaults apply where the tune states nothing of its own.
  const defaults = header.filter((line) => {
    const field = fieldOf(line);
    if (!field) return true;
    return HEADER_DEFAULTS.has(field.letter) && !own.has(field.letter);
  });
  if (composers.length === 0) {
    for (const line of defaults) {
      const field = fieldOf(line);
      if (field?.letter === 'C') {
        const value = decodeAbcText(stripComment(field.value)).trim();
        if (value) composers.push(value);
      }
    }
  }
  const text = body.join('\n').trim();
  return {
    title: title || 'Untitled',
    composer: composers.join(', '),
    chunk: defaults.length > 0 ? `${defaults.join('\n')}\n${text}\n` : `${text}\n`,
  };
}

/** Cheap facts about one tune for the browser's badges and filters. */
export interface AbcTuneMeta {
  /** R: — the rhythm or feel ("Swing", "reel"). */
  style?: string;
  /** The key as the browser prints it ("G", "E minor", "D dorian"). */
  key?: string;
  /** Any written notes (a chords-over-rests chart has none). */
  melody: boolean;
  /** Any w: lyrics. */
  lyrics: boolean;
}

const MODE_WORDS: [RegExp, string][] = [
  [/^(?:m|min|minor|aeo|aeolian)$/i, 'minor'],
  [/^(?:dor|dorian)$/i, 'dorian'],
  [/^(?:phr|phrygian)$/i, 'phrygian'],
  [/^(?:lyd|lydian)$/i, 'lydian'],
  [/^(?:mix|mixolydian)$/i, 'mixolydian'],
  [/^(?:loc|locrian)$/i, 'locrian'],
];

/** "Gm" → "G minor", "Dmix" → "D mixolydian", "Bb" → "Bb". */
export function abcKeyLabel(value: string): string | undefined {
  const m = /^([A-G])([#b]?)\s*([A-Za-z]*)/.exec(value.trim());
  if (!m) return undefined;
  const tonic = `${m[1]}${m[2]}`;
  const word = m[3];
  if (!word || /^(?:maj|major|ion|ionian)$/i.test(word)) return tonic;
  for (const [re, label] of MODE_WORDS) {
    if (re.test(word) || re.test(word.slice(0, 3))) return `${tonic} ${label}`;
  }
  return tonic;
}

export function peekAbcMeta(chunk: string): AbcTuneMeta {
  let style: string | undefined;
  let key: string | undefined;
  let melody = false;
  let lyrics = false;
  let inBody = false;
  for (const raw of lines(chunk)) {
    const line = stripComment(raw);
    const field = fieldOf(line);
    if (field && !(inBody && /^[A-Ga-g]$/.test(field.letter) && /^[|:\]]/.test(field.value))) {
      if (field.letter === 'R' && !style && field.value) style = decodeAbcText(field.value);
      if (field.letter === 'K') {
        if (!inBody && key === undefined) key = abcKeyLabel(field.value);
        inBody = true;
      }
      if (field.letter === 'w' && field.value.replace(/[\s*_|-]/g, '')) lyrics = true;
      continue;
    }
    if (!inBody || melody) continue;
    const music = line
      .replace(/"[^"]*"/g, '')
      .replace(/![^!]*!/g, '')
      .replace(/\+[^+\s]*\+/g, '')
      .replace(/\[[A-Za-z]:[^\]]*\]/g, '');
    if (/[A-Ga-g]/.test(music)) melody = true;
  }
  return { ...(style ? { style } : {}), ...(key ? { key } : {}), melody, lyrics };
}
